import BACKEND_CONFIG from './backendConfig';
import { REMOTE_FLOW_HTTP_BASE } from './remoteFlowHost';

const trimSlash = (value: string): string => value.replace(/\/+$/, '');

// MMEC unified stack on 212 — flow API is local :5001. Override via MMEC_FLOW_BASE_URL / REACT_APP_REMOTE_FLOW_API_URL.
const REMOTE_BACKEND_URL = trimSlash(
  process.env.MMEC_FLOW_BASE_URL ||
    process.env.REACT_APP_REMOTE_FLOW_API_URL ||
    REMOTE_FLOW_HTTP_BASE
);

/** Optional: public HTTPS base for the remote flow API (browser calls this directly). */
const REMOTE_FLOW_PUBLIC_URL_RAW = trimSlash((process.env.REACT_APP_REMOTE_FLOW_PUBLIC_URL || '').trim());

/** Build-time: force remote-flow routes (theta, stable accumulated-data, etc.) to same-origin so Caddy proxies. */
const FORCE_SAME_ORIGIN_REMOTE_FLOW =
  String(process.env.REACT_APP_FORCE_SAME_ORIGIN_REMOTE_FLOW || '').trim() === '1';

const IS_BROWSER_HTTPS =
  typeof window !== 'undefined' && window.location.protocol === 'https:';

function hostnameLooksClientUnreachable(hostname: string): boolean {
  const h = (hostname || '').toLowerCase().trim();
  if (!h) return true;
  if (h === 'localhost') return false;
  if (h.endsWith('.local') || h.endsWith('.internal')) return true;
  if (/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (/^192\.168\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  const m = /^172\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/.exec(h);
  if (m) {
    const second = parseInt(m[1], 10);
    if (second >= 16 && second <= 31) return true;
  }
  return false;
}

/**
 * Sanitized https public base for browser calls. Empty if unset, non-https, unparseable, or hostname
 * will not resolve from typical user networks (private / mDNS-style TLDs) — caller should fall back to same-origin.
 */
function safeHttpsRemoteFlowPublicOrigin(): string {
  const raw = REMOTE_FLOW_PUBLIC_URL_RAW;
  if (!raw || !raw.toLowerCase().startsWith('https://')) return '';
  try {
    const u = new URL(raw);
    if (!u.hostname) return '';
    if (hostnameLooksClientUnreachable(u.hostname)) return '';
    return trimSlash(u.origin);
  } catch {
    return '';
  }
}

const REMOTE_REQUIRES_PROXY_FALLBACK =
  IS_BROWSER_HTTPS &&
  REMOTE_BACKEND_URL.startsWith('http://') &&
  !safeHttpsRemoteFlowPublicOrigin();

/**
 * Local (this stack) API base: Legacy RTL, auth, health, accumulated-data when source≠stable, etc.
 * - If REACT_APP_LOCAL_API_URL or REACT_APP_API_URL is set at build time, use it.
 * - Else in the browser, use same-origin (empty string → caller uses relative `/api/...`).
 *   Docker/nginx frontends proxy `/api/` to Flask; hardcoding PRIMARY_URL breaks that and shows 0 rows.
 * - Else (Node/tests), fall back to PRIMARY_URL.
 */
export const getLocalBackendUrl = (): string => {
  const fromEnv = (process.env.REACT_APP_LOCAL_API_URL || process.env.REACT_APP_API_URL || '').trim();
  if (fromEnv) {
    return trimSlash(fromEnv);
  }
  if (typeof window !== 'undefined') {
    return '';
  }
  return trimSlash(BACKEND_CONFIG.PRIMARY_URL);
};

/**
 * Base URL for remote-owned APIs: stable accumulated-data, flow, OIS, theta-stream, `/api/theta/*`.
 */
export const getEffectiveRemoteBaseUrl = (): string => {
  if (typeof window !== 'undefined' && FORCE_SAME_ORIGIN_REMOTE_FLOW) {
    return trimSlash(window.location.origin);
  }
  // Stale build env may still point at decommissioned 191 — never use from browser.
  if (
    typeof window !== 'undefined' &&
    (REMOTE_BACKEND_URL.includes('212.193.3.56') || REMOTE_FLOW_PUBLIC_URL_RAW.includes('212.193.3.56'))
  ) {
    return trimSlash(window.location.origin);
  }
  const pub = safeHttpsRemoteFlowPublicOrigin();
  if (pub) {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
      try {
        if (trimSlash(window.location.origin).toLowerCase() === pub.toLowerCase()) {
          return trimSlash(window.location.origin);
        }
      } catch {
        /* ignore */
      }
    }
    return pub;
  }
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    REMOTE_BACKEND_URL.startsWith('http://')
  ) {
    return trimSlash(window.location.origin);
  }
  return REMOTE_BACKEND_URL;
};

export const getRemoteBackendUrl = (): string => REMOTE_BACKEND_URL;

// Paths starting with these prefixes use getEffectiveRemoteBaseUrl() (remote flow VPS via same-origin proxy).
// /api/ois matches the entire OIS tree: report-view, report-full-text, intel/bundle, intel/push, report-arabic-*, symbol-profiles, etc.
const REMOTE_PREFIXES = [
  '/api/options-flow',
  '/api/flow',
  '/api/flow-opportunities',
  '/api/flow-trades',
  '/api/scan-flow',
  '/api/flow-stats',
  '/api/smart-flow',
  '/api/flow-engine',
  '/api/ois',
  '/api/theta-stream',
  // IV tracking (Barchart-compatible IV rank/percentile) is served by remote-flow-backend.
  '/api/iv-tracking',
  // Edge Journey scanner + fingerprint (iv_snapshots history lives on the remote host).
  '/api/edge',
];

/** Flask on consumer host serves these; may proxy to remote server-side. Never send browser direct to remote :5001. */
const LOCAL_HISTORICAL_PREFIXES = [
  '/api/historical/eod',
  '/api/historical/intraday-gamma-shift',
  '/api/historical/contract-tape-by-day',
  '/api/historical/equity-bars',
  '/api/historical/intraday-options',
  '/api/historical-snapshots',
];

const tryReadQuerySource = (cleanEndpoint: string): string => {
  try {
    const url = new URL(cleanEndpoint, 'http://local');
    return (url.searchParams.get('source') || '').trim().toLowerCase();
  } catch {
    const lower = cleanEndpoint.toLowerCase();
    const match = lower.match(/[?&]source=([^&]+)/);
    return match ? decodeURIComponent(match[1] || '').trim().toLowerCase() : '';
  }
};

const isThetaTerminalPath = (clean: string): boolean =>
  clean === '/api/theta' ||
  clean.startsWith('/api/theta/') ||
  clean.startsWith('/api/theta?');

export const isRemoteEndpoint = (endpoint: string): boolean => {
  const clean = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  if (LOCAL_HISTORICAL_PREFIXES.some((prefix) => clean.startsWith(prefix))) {
    return false;
  }
  /** Served only by consumer Flask + local storage; remote flow API does not expose this path. */
  if (clean.startsWith('/api/options-flow/pattern-candidates')) {
    return false;
  }
  if (clean.startsWith('/api/options-flow/pattern-analyze-llm')) {
    return false;
  }
  if (clean.startsWith('/api/accumulated-data')) {
    const source = tryReadQuerySource(clean);
    if (source === 'stable') return true;
    return false;
  }
  if (isThetaTerminalPath(clean)) return true;
  return REMOTE_PREFIXES.some((prefix) => clean.startsWith(prefix));
};

export const resolveApiBaseUrl = (endpoint: string): string => {
  return isRemoteEndpoint(endpoint) ? getEffectiveRemoteBaseUrl() : getLocalBackendUrl();
};

export const getRoutingDebugInfo = () => ({
  localBackend: getLocalBackendUrl(),
  remoteBackend: REMOTE_BACKEND_URL,
  remoteFlowPublicUrlRaw: REMOTE_FLOW_PUBLIC_URL_RAW || null,
  remoteFlowPublicUrlSanitized: safeHttpsRemoteFlowPublicOrigin() || null,
  forceSameOriginRemoteFlow: FORCE_SAME_ORIGIN_REMOTE_FLOW,
  effectiveRemoteBackend: getEffectiveRemoteBaseUrl(),
  sameOriginRemoteFlow: getEffectiveRemoteBaseUrl() === getLocalBackendUrl(),
  httpsMixedContentFallback: REMOTE_REQUIRES_PROXY_FALLBACK,
  remotePrefixes: REMOTE_PREFIXES,
});
